import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { api, ApiError } from './api'
import type {
  AadhaarStatus,
  AdminInterestOut,
  AdminJobDetail,
  AdminJobSummary,
  AllocationOut,
  CandidateNotary,
  ClaimPreview,
  ClaimResponse,
  DocumentRef,
  DraftOut,
  DraftPayload,
  DraftValidation,
  HealthcareRepIn,
  HealthcareRepOut,
  InterestCounts,
  InterestIn,
  InterestOut,
  InterestStatus,
  MeOut,
  MetricsSummary,
  NotaryIn,
  NotaryJobDetail,
  NotaryJobSummary,
  NotaryMeOut,
  NotaryOut,
  NotaryUpdate,
  ServiceAreaIn,
  SubmitResponse,
  TemplateDetail,
  TemplateSummary,
  TemplateVersionIn,
  WillOut,
} from './types'

export const keys = {
  me: ['me'] as const,
  draft: ['draft'] as const,
  draftValidation: ['draft', 'validation'] as const,
  aadhaar: ['me', 'aadhaar'] as const,
  reps: ['healthcare-reps'] as const,
  wills: ['living-wills'] as const,
  will: (id: string) => ['living-wills', id] as const,
  adminJobs: (params: Record<string, string | boolean | undefined>) =>
    ['admin', 'jobs', params] as const,
  adminJob: (id: string) => ['admin', 'jobs', id] as const,
  candidates: (id: string) => ['admin', 'jobs', id, 'candidates'] as const,
  notaries: (params: Record<string, string | undefined>) => ['admin', 'notaries', params] as const,
  notary: (id: string) => ['admin', 'notaries', id] as const,
  metrics: ['admin', 'metrics'] as const,
  notaryMe: ['notary', 'me'] as const,
  notaryJobs: ['notary', 'jobs'] as const,
  notaryJob: (id: string) => ['notary', 'jobs', id] as const,
  templates: ['admin', 'templates'] as const,
  template: (id: string) => ['admin', 'templates', id] as const,
  myInterest: ['interest'] as const,
  adminInterest: (params: Record<string, string | undefined>) =>
    ['admin', 'interest', params] as const,
  interestCounts: ['admin', 'interest', 'summary'] as const,
}

// --- identity ---------------------------------------------------------------

/**
 * The single source of truth for "who am I and may I see the admin area".
 * `is_admin` gates the *UI only* — every admin endpoint re-checks the role, so
 * a tampered client gets a 403 rather than data.
 */
export function useMe(options?: Partial<UseQueryOptions<MeOut, ApiError>>) {
  return useQuery<MeOut, ApiError>({
    queryKey: keys.me,
    queryFn: () => api.get<MeOut>('/me'),
    staleTime: 30_000,
    retry: false,
    ...options,
  })
}

// --- wizard draft -----------------------------------------------------------

export function useDraft(enabled = true) {
  return useQuery<DraftOut, ApiError>({
    queryKey: keys.draft,
    queryFn: () => api.get<DraftOut>('/drafts/current'),
    enabled,
    retry: false,
    // The draft is a working copy edited in this tab; refetching under it on
    // window focus would fight the user's typing.
    refetchOnWindowFocus: false,
  })
}

/**
 * Server-side completeness. Deliberately *not* re-implemented in the browser:
 * two copies of the rules drift, and the server's copy is the one that decides.
 */
export function useDraftValidation(enabled = true) {
  return useQuery<DraftValidation, ApiError>({
    queryKey: keys.draftValidation,
    queryFn: () => api.get<DraftValidation>('/drafts/current/validation'),
    enabled,
    retry: false,
  })
}

export function useSaveDraft() {
  const queryClient = useQueryClient()
  return useMutation<
    DraftOut,
    ApiError,
    { currentStep: number; payload: DraftPayload; version: number }
  >({
    mutationFn: ({ currentStep, payload, version }) =>
      api.put<DraftOut>(
        '/drafts/current',
        { current_step: currentStep, payload },
        { headers: { 'If-Match': String(version) } },
      ),
    onSuccess: (draft) => {
      queryClient.setQueryData(keys.draft, draft)
    },
  })
}

export function useSetAadhaar() {
  const queryClient = useQueryClient()
  return useMutation<AadhaarStatus, ApiError, string>({
    // Its own endpoint, on purpose: the number goes straight to an encrypted
    // column and is never written into the draft's plain JSONB payload.
    mutationFn: (aadhaar) => api.post<AadhaarStatus>('/drafts/current/aadhaar', { aadhaar }),
    onSuccess: (status) => {
      queryClient.setQueryData(keys.aadhaar, status)
      void queryClient.invalidateQueries({ queryKey: keys.draftValidation })
      void queryClient.invalidateQueries({ queryKey: keys.me })
    },
  })
}

export function useAadhaarStatus(enabled = true) {
  return useQuery<AadhaarStatus, ApiError>({
    queryKey: keys.aadhaar,
    queryFn: () => api.get<AadhaarStatus>('/me/aadhaar'),
    enabled,
    retry: false,
  })
}

export function useRenderPreview() {
  return useMutation<DocumentRef, ApiError, void>({
    mutationFn: () => api.post<DocumentRef>('/drafts/current/preview'),
  })
}

export function useSubmitDraft() {
  const queryClient = useQueryClient()
  return useMutation<SubmitResponse, ApiError, void>({
    mutationFn: () => api.post<SubmitResponse>('/drafts/current/submit', { confirm: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.me })
      void queryClient.invalidateQueries({ queryKey: keys.wills })
      queryClient.removeQueries({ queryKey: keys.draft })
    },
  })
}

export function useDiscardDraft() {
  const queryClient = useQueryClient()
  return useMutation<void, ApiError, void>({
    mutationFn: () => api.del<void>('/drafts/current'),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: keys.draft })
      void queryClient.invalidateQueries({ queryKey: keys.me })
      void queryClient.invalidateQueries({ queryKey: keys.draftValidation })
    },
  })
}

// --- representatives --------------------------------------------------------

export function useReps(enabled = true) {
  return useQuery<HealthcareRepOut[], ApiError>({
    queryKey: keys.reps,
    queryFn: () => api.get<HealthcareRepOut[]>('/healthcare-reps'),
    enabled,
    retry: false,
  })
}

function useRepInvalidation() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: keys.reps })
    void queryClient.invalidateQueries({ queryKey: keys.draftValidation })
  }
}

export function useAddRep() {
  const invalidate = useRepInvalidation()
  return useMutation<HealthcareRepOut, ApiError, HealthcareRepIn>({
    mutationFn: (body) => api.post<HealthcareRepOut>('/healthcare-reps', body),
    onSuccess: invalidate,
  })
}

export function useUpdateRep() {
  const invalidate = useRepInvalidation()
  return useMutation<HealthcareRepOut, ApiError, { id: string; body: HealthcareRepIn }>({
    mutationFn: ({ id, body }) => api.put<HealthcareRepOut>(`/healthcare-reps/${id}`, body),
    onSuccess: invalidate,
  })
}

export function useDeleteRep() {
  const invalidate = useRepInvalidation()
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.del<void>(`/healthcare-reps/${id}`),
    onSuccess: invalidate,
  })
}

// --- living wills -----------------------------------------------------------

export function useMyWills(enabled = true) {
  return useQuery<WillOut[], ApiError>({
    queryKey: keys.wills,
    queryFn: () => api.get<WillOut[]>('/living-wills'),
    enabled,
    retry: false,
  })
}

export function useMyWill(id: string | undefined) {
  return useQuery<WillOut, ApiError>({
    queryKey: keys.will(id ?? ''),
    queryFn: () => api.get<WillOut>(`/living-wills/${id}`),
    enabled: Boolean(id),
    retry: false,
  })
}

// --- admin ------------------------------------------------------------------

export interface JobQueryParams {
  status?: string
  pincode?: string
  q?: string
  needs_attention?: boolean
}

export function useAdminJobs(params: JobQueryParams) {
  const search = new URLSearchParams()
  if (params.status) search.set('status', params.status)
  if (params.pincode) search.set('pincode', params.pincode)
  if (params.q) search.set('q', params.q)
  if (params.needs_attention) search.set('needs_attention', 'true')
  const query = search.toString()

  return useQuery<AdminJobSummary[], ApiError>({
    queryKey: keys.adminJobs({ ...params }),
    queryFn: () => api.get<AdminJobSummary[]>(`/admin/living-wills${query ? `?${query}` : ''}`),
    retry: false,
  })
}

export function useAdminJob(id: string | undefined) {
  return useQuery<AdminJobDetail, ApiError>({
    queryKey: keys.adminJob(id ?? ''),
    queryFn: () => api.get<AdminJobDetail>(`/admin/living-wills/${id}`),
    enabled: Boolean(id),
    retry: false,
  })
}

export function useCandidateNotaries(id: string | undefined) {
  return useQuery<CandidateNotary[], ApiError>({
    queryKey: keys.candidates(id ?? ''),
    queryFn: () => api.get<CandidateNotary[]>(`/admin/living-wills/${id}/candidate-notaries`),
    enabled: Boolean(id),
    retry: false,
  })
}

export function useAdminMetrics() {
  return useQuery<MetricsSummary, ApiError>({
    queryKey: keys.metrics,
    queryFn: () => api.get<MetricsSummary>('/admin/metrics/summary'),
    retry: false,
  })
}

function useJobInvalidation(id: string | undefined) {
  const queryClient = useQueryClient()
  return () => {
    if (id) {
      void queryClient.invalidateQueries({ queryKey: keys.adminJob(id) })
      void queryClient.invalidateQueries({ queryKey: keys.candidates(id) })
    }
    void queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] })
    void queryClient.invalidateQueries({ queryKey: keys.metrics })
  }
}

export interface AllocateInput {
  notary_id: string
  override?: boolean
  override_reason?: string | null
}

export function useAllocate(jobId: string | undefined) {
  const invalidate = useJobInvalidation(jobId)
  return useMutation<AllocationOut, ApiError, AllocateInput>({
    mutationFn: (body) => api.post<AllocationOut>(`/admin/living-wills/${jobId}/allocate`, body),
    onSuccess: invalidate,
  })
}

export function useReassign(jobId: string | undefined) {
  const invalidate = useJobInvalidation(jobId)
  return useMutation<AllocationOut, ApiError, { notary_id: string; reason: string }>({
    mutationFn: (body) => api.post<AllocationOut>(`/admin/living-wills/${jobId}/reassign`, body),
    onSuccess: invalidate,
  })
}

export function useRecordDecision(jobId: string | undefined) {
  const invalidate = useJobInvalidation(jobId)
  return useMutation<
    AllocationOut,
    ApiError,
    { allocationId: string; decision: 'accept' | 'reject'; reason?: string }
  >({
    mutationFn: ({ allocationId, decision, reason }) =>
      api.post<AllocationOut>(`/admin/allocations/${allocationId}/decide`, { decision, reason }),
    onSuccess: invalidate,
  })
}

export function useResendOffer(jobId: string | undefined) {
  const invalidate = useJobInvalidation(jobId)
  return useMutation<unknown, ApiError, string>({
    mutationFn: (allocationId) => api.post(`/admin/allocations/${allocationId}/resend-email`),
    onSuccess: invalidate,
  })
}

export function useCompleteJob(jobId: string | undefined) {
  const invalidate = useJobInvalidation(jobId)
  return useMutation<WillOut, ApiError, void>({
    mutationFn: () => api.post<WillOut>(`/admin/living-wills/${jobId}/complete`),
    onSuccess: invalidate,
  })
}

export function useCancelJob(jobId: string | undefined) {
  const invalidate = useJobInvalidation(jobId)
  return useMutation<WillOut, ApiError, string>({
    mutationFn: (reason) => api.post<WillOut>(`/admin/living-wills/${jobId}/cancel`, { reason }),
    onSuccess: invalidate,
  })
}

// --- notary portal ----------------------------------------------------------

/**
 * The signed-in notary. Plays the same role for `/notary/*` that `useMe` plays
 * for the citizen and admin areas: it is what the route guard waits on, and a
 * 403 from it is what sends a non-notary away.
 */
export function useNotaryMe(options?: Partial<UseQueryOptions<NotaryMeOut, ApiError>>) {
  return useQuery<NotaryMeOut, ApiError>({
    queryKey: keys.notaryMe,
    queryFn: () => api.get<NotaryMeOut>('/notary/me'),
    staleTime: 30_000,
    retry: false,
    ...options,
  })
}

export function useNotaryJobs(enabled = true) {
  return useQuery<NotaryJobSummary[], ApiError>({
    queryKey: keys.notaryJobs,
    queryFn: () => api.get<NotaryJobSummary[]>('/notary/jobs'),
    enabled,
    retry: false,
  })
}

export function useNotaryJob(id: string | undefined) {
  return useQuery<NotaryJobDetail, ApiError>({
    queryKey: keys.notaryJob(id ?? ''),
    queryFn: () => api.get<NotaryJobDetail>(`/notary/jobs/${id}`),
    enabled: Boolean(id),
    retry: false,
  })
}

/**
 * Resolve a claim code without acting on it.
 *
 * A mutation rather than a query even though it reads: it fires when the
 * notary submits the form, not when a key is pressed, and caching a lookup
 * keyed on a live credential is not something to leave lying in the query
 * cache. The API takes it as a POST for the same reason.
 */
export function useLookupClaim() {
  return useMutation<ClaimPreview, ApiError, string>({
    mutationFn: (code) => api.post<ClaimPreview>('/notary/claims/lookup', { code }),
  })
}

export function useClaimJob() {
  const queryClient = useQueryClient()
  return useMutation<ClaimResponse, ApiError, string>({
    mutationFn: (code) => api.post<ClaimResponse>('/notary/claims', { code }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.notaryJobs })
      void queryClient.invalidateQueries({ queryKey: keys.notaryMe })
    },
  })
}

// --- document templates -----------------------------------------------------

export function useTemplates() {
  return useQuery<TemplateSummary[], ApiError>({
    queryKey: keys.templates,
    queryFn: () => api.get<TemplateSummary[]>('/admin/templates'),
    retry: false,
  })
}

export function useTemplate(id: string | undefined) {
  return useQuery<TemplateDetail, ApiError>({
    queryKey: keys.template(id ?? ''),
    queryFn: () => api.get<TemplateDetail>(`/admin/templates/${id}`),
    enabled: Boolean(id),
    retry: false,
  })
}

function useTemplateInvalidation() {
  const queryClient = useQueryClient()
  return () => void queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
}

export function useCreateTemplateVersion() {
  const invalidate = useTemplateInvalidation()
  return useMutation<TemplateDetail, ApiError, TemplateVersionIn>({
    mutationFn: (body) => api.post<TemplateDetail>('/admin/templates', body),
    onSuccess: invalidate,
  })
}

export function useActivateTemplate() {
  const invalidate = useTemplateInvalidation()
  return useMutation<TemplateSummary, ApiError, string>({
    mutationFn: (id) => api.post<TemplateSummary>(`/admin/templates/${id}/activate`),
    onSuccess: invalidate,
  })
}

// --- interest register ------------------------------------------------------

export function useMyInterest(enabled = true) {
  return useQuery<InterestOut[], ApiError>({
    queryKey: keys.myInterest,
    queryFn: () => api.get<InterestOut[]>('/interest'),
    enabled,
    retry: false,
  })
}

export function useRegisterInterest() {
  const queryClient = useQueryClient()
  return useMutation<InterestOut, ApiError, InterestIn>({
    mutationFn: (body) => api.post<InterestOut>('/interest', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.myInterest }),
  })
}

export function useWithdrawInterest() {
  const queryClient = useQueryClient()
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.del<void>(`/interest/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.myInterest }),
  })
}

export interface InterestQueryParams {
  topic?: string
  status?: string
}

export function useAdminInterest(params: InterestQueryParams) {
  const search = new URLSearchParams()
  if (params.topic) search.set('topic', params.topic)
  if (params.status) search.set('status', params.status)
  const query = search.toString()

  return useQuery<AdminInterestOut[], ApiError>({
    queryKey: keys.adminInterest({ ...params }),
    queryFn: () => api.get<AdminInterestOut[]>(`/admin/interest${query ? `?${query}` : ''}`),
    retry: false,
  })
}

export function useInterestCounts() {
  return useQuery<InterestCounts, ApiError>({
    queryKey: keys.interestCounts,
    queryFn: () => api.get<InterestCounts>('/admin/interest/summary'),
    retry: false,
  })
}

export function useSetInterestStatus() {
  const queryClient = useQueryClient()
  return useMutation<AdminInterestOut, ApiError, { id: string; status: InterestStatus }>({
    mutationFn: ({ id, status }) =>
      api.patch<AdminInterestOut>(`/admin/interest/${id}`, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'interest'] })
    },
  })
}

// --- notaries ---------------------------------------------------------------

export function useNotaries(params: { q?: string; pincode?: string; status?: string }) {
  const search = new URLSearchParams()
  if (params.q) search.set('q', params.q)
  if (params.pincode) search.set('pincode', params.pincode)
  if (params.status) search.set('status', params.status)
  const query = search.toString()

  return useQuery<NotaryOut[], ApiError>({
    queryKey: keys.notaries({ ...params }),
    queryFn: () => api.get<NotaryOut[]>(`/admin/notaries${query ? `?${query}` : ''}`),
    retry: false,
  })
}

export function useNotary(id: string | undefined) {
  return useQuery<NotaryOut, ApiError>({
    queryKey: keys.notary(id ?? ''),
    queryFn: () => api.get<NotaryOut>(`/admin/notaries/${id}`),
    enabled: Boolean(id),
    retry: false,
  })
}

function useNotaryInvalidation() {
  const queryClient = useQueryClient()
  return () => void queryClient.invalidateQueries({ queryKey: ['admin', 'notaries'] })
}

export function useCreateNotary() {
  const invalidate = useNotaryInvalidation()
  return useMutation<NotaryOut, ApiError, NotaryIn>({
    mutationFn: (body) => api.post<NotaryOut>('/admin/notaries', body),
    onSuccess: invalidate,
  })
}

export function useUpdateNotary(id: string | undefined) {
  const invalidate = useNotaryInvalidation()
  return useMutation<NotaryOut, ApiError, NotaryUpdate>({
    mutationFn: (body) => api.patch<NotaryOut>(`/admin/notaries/${id}`, body),
    onSuccess: invalidate,
  })
}

export function useAddServiceArea(id: string | undefined) {
  const invalidate = useNotaryInvalidation()
  return useMutation<NotaryOut, ApiError, ServiceAreaIn>({
    mutationFn: (body) => api.post<NotaryOut>(`/admin/notaries/${id}/service-areas`, body),
    onSuccess: invalidate,
  })
}

export function useDeleteServiceArea(id: string | undefined) {
  const invalidate = useNotaryInvalidation()
  return useMutation<void, ApiError, string>({
    mutationFn: (areaId) => api.del<void>(`/admin/notaries/${id}/service-areas/${areaId}`),
    onSuccess: invalidate,
  })
}
